class Testimonial extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.cards = [];
    this.images = [];
    this.isMobile = false;
    this.autoplayEnabled = false;
    this.autoplaySeconds = 5;
    this.autoplayIntervalId = null;
    this.isAutoplayLocked = false;

    this.handleCardClick = this.handleCardClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('.testimonial__card'));
    this.images = Array.from(this.querySelectorAll('.testimonial__main-img'));
    this.cardsContainer = this.querySelector('.testimonial__cards');
    this.pagination = this.querySelector('.testimonial__pagination');
    this.totalCards = this.cards.length;
    
    if (this.totalCards === 0) {
      return;
    }

    // Match the simpler autoplay pattern used in `section-our-expertise.js`.
    this.autoplayEnabled = this.getAttribute('data-autoplay-enabled') === 'true';
    this.autoplaySeconds = Math.max(
      2,
      parseInt(this.getAttribute('data-autoplay-seconds'), 10) || 5
    );

    // Check if mobile
    this.checkMobile();

    // Add click handlers to cards (desktop only)
    if (!this.isMobile) {
      this.cards.forEach((card, index) => {
        card.addEventListener('click', () => this.handleCardClick(index));
      });
    }

    // Initialize card positions
    this.updateCardPositions();

    // Setup mobile pagination
    if (this.isMobile) {
      this.setupMobilePagination();
    }

    if (this.autoplayEnabled && this.totalCards > 1) {
      this.startAutoplay();
    }

    // Handle resize
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    this.stopAutoplay();

    this.cards.forEach(card => {
      card.removeEventListener('click', this.handleCardClick);
    });
    window.removeEventListener('resize', this.handleResize);
    if (this.cardsContainer) {
      this.cardsContainer.removeEventListener('scroll', this.handleScroll);
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    this.autoplayIntervalId = window.setInterval(() => {
      this.advanceToNext();
    }, this.autoplaySeconds * 1000);
  }

  stopAutoplay() {
    if (this.autoplayIntervalId) {
      window.clearInterval(this.autoplayIntervalId);
      this.autoplayIntervalId = null;
    }
  }

  advanceToNext() {
    if (this.isAutoplayLocked) return;

    this.isAutoplayLocked = true;

    const nextIndex = this.getNextIndex(this.currentIndex);
    if (this.isMobile) {
      this.currentIndex = nextIndex;
      // On mobile the cards are a scroll-snap list; update state immediately
      // and let the scroll event confirm it.
      this.updatePagination(nextIndex);
      this.updateActiveCardOnScroll(nextIndex);
      this.scrollToCard(nextIndex);

      setTimeout(() => {
        this.isAutoplayLocked = false;
      }, 900);
      return;
    }

    this.currentIndex = nextIndex;
    this.updateCardPositions();
    this.switchImage(nextIndex);

    setTimeout(() => {
      this.isAutoplayLocked = false;
    }, 650);
  }

  checkMobile() {
    this.isMobile = window.innerWidth <= 1080;
  }

  handleResize() {
    const wasMobile = this.isMobile;
    this.checkMobile();

    // If mode changed, reinitialize
    if (wasMobile !== this.isMobile) {
      this.updateCardPositions();
      if (this.isMobile) {
        this.setupMobilePagination();
      }
    }
  }

  handleCardClick(index) {
    if (this.isMobile) return; // No clicking on mobile, scroll instead
    if (index === this.currentIndex) return; // Already active

    this.currentIndex = index;
    this.updateCardPositions();
    this.switchImage(index);
  }

  updateCardPositions() {
    if (this.isMobile) {
      // On mobile, all cards are equal, no special positioning
      this.cards.forEach(card => {
        card.classList.remove('testimonial__card--active', 'testimonial__card--next', 'testimonial__card--hidden', 'testimonial__card--fading-out', 'testimonial__card--moving-in');
        card.style.removeProperty('left');
      });
      // First card starts as "active" for styling
      if (this.cards[this.currentIndex]) {
        this.cards[this.currentIndex].classList.add('testimonial__card--active');
      }
      return;
    }

    // Desktop: position cards based on current index with circular logic
    const cardWidth = 440; // Width of next cards
    const gap = 20; // Gap between cards
    const activeCardWidth = 480; // Width of active card
    const startLeft = 540; // Starting position for first next card

    // Find the currently active card (the one being replaced)
    const currentlyActiveCard = this.cards.find(card => card.classList.contains('testimonial__card--active'));
    
    if (currentlyActiveCard && currentlyActiveCard !== this.cards[this.currentIndex]) {
      // Step 1: Fade out the old active card (stays at left: 0)
      currentlyActiveCard.classList.add('testimonial__card--fading-out');
      
      // Step 2: Immediately position all cards (new card starts moving in)
      this.cards.forEach((card, index) => {
        if (card === currentlyActiveCard) {
          // Skip the fading-out card, it will be handled after animation
          return;
        }
        
        // Remove all state classes except fading-out
        card.classList.remove('testimonial__card--active', 'testimonial__card--next', 'testimonial__card--hidden', 'testimonial__card--moving-in', 'testimonial__card--invisible');
        
        if (index === this.currentIndex) {
          // New active card: move to active position immediately
          card.classList.add('testimonial__card--active');
          card.style.left = '0';
        } else {
          // All other cards are positioned as "next" in circular order
          let positionIndex = (index - this.currentIndex - 1 + this.totalCards) % this.totalCards;
          card.classList.add('testimonial__card--next');
          card.style.left = `${startLeft + (positionIndex * (cardWidth + gap))}px`;
        }
      });
      
      // Step 3: After fade-out completes, reposition the old active card
      setTimeout(() => {
        currentlyActiveCard.classList.remove('testimonial__card--fading-out', 'testimonial__card--active');
        
        // Find its new position in the circular order
        const oldIndex = Array.from(this.cards).indexOf(currentlyActiveCard);
        let positionIndex = (oldIndex - this.currentIndex - 1 + this.totalCards) % this.totalCards;
        
        // Make it invisible while repositioning
        currentlyActiveCard.classList.add('testimonial__card--invisible');
        currentlyActiveCard.classList.add('testimonial__card--next');
        currentlyActiveCard.style.left = `${startLeft + (positionIndex * (cardWidth + gap))}px`;
        
        // Fade it back in at new position
        setTimeout(() => {
          currentlyActiveCard.classList.remove('testimonial__card--invisible');
        }, 100);
      }, 500); // Wait for full fade-out
    } else {
      // Initial load - update immediately
      this.cards.forEach((card, index) => {
        card.classList.remove('testimonial__card--active', 'testimonial__card--next', 'testimonial__card--hidden', 'testimonial__card--fading-out', 'testimonial__card--moving-in');
        
        if (index === this.currentIndex) {
          card.classList.add('testimonial__card--active');
          card.style.left = '0';
        } else {
          // Calculate position for next cards in circular order
          let positionIndex = (index - this.currentIndex - 1 + this.totalCards) % this.totalCards;
          card.classList.add('testimonial__card--next');
          card.style.left = `${startLeft + (positionIndex * (cardWidth + gap))}px`;
        }
      });
    }
  }

  getNextIndex(current) {
    // Circular logic: if we're at the last card, next is first card
    return (current + 1) % this.totalCards;
  }

  switchImage(index) {
    // Hide all images
    this.images.forEach(img => {
      img.classList.add('hidden');
    });

    // Show selected image
    if (this.images[index]) {
      this.images[index].classList.remove('hidden');
    }
  }


  setupMobilePagination() {
    if (!this.cardsContainer || !this.pagination) return;

    // Clear existing pagination
    this.pagination.innerHTML = '';

    // Create pagination bullets
    this.cards.forEach((card, index) => {
      const bullet = document.createElement('button');
      bullet.classList.add('swiper-pagination-bullet');
      if (index === 0) {
        bullet.classList.add('swiper-pagination-bullet-active');
      }
      bullet.setAttribute('aria-label', `Go to testimonial ${index + 1}`);
      bullet.addEventListener('click', () => {
        this.scrollToCard(index);
      });
      this.pagination.appendChild(bullet);
    });

    // Handle scroll to update pagination
    this.cardsContainer.addEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    if (!this.isMobile || !this.cardsContainer) return;

    // Calculate which card is currently in view
    const scrollLeft = this.cardsContainer.scrollLeft;
    const containerWidth = this.cardsContainer.offsetWidth;
    
    // Each card takes up exactly the full container width
    const currentCard = Math.round(scrollLeft / containerWidth);

    // Update pagination
    this.updatePagination(currentCard);

    // Update active card styling and image
    this.updateActiveCardOnScroll(currentCard);
  }

  updatePagination(activeIndex) {
    if (!this.pagination) return;
    const bullets = this.pagination.querySelectorAll('.swiper-pagination-bullet');
    if (!bullets.length) return;

    bullets.forEach((bullet, index) => {
      if (index === activeIndex) {
        bullet.classList.add('swiper-pagination-bullet-active');
      } else {
        bullet.classList.remove('swiper-pagination-bullet-active');
      }
    });
  }

  updateActiveCardOnScroll(activeIndex) {
    // Update card active class
    this.cards.forEach((card, index) => {
      if (index === activeIndex) {
        card.classList.add('testimonial__card--active');
      } else {
        card.classList.remove('testimonial__card--active');
      }
    });

    // Switch image
    this.switchImage(activeIndex);
  }

  scrollToCard(index) {
    if (!this.cardsContainer || !this.cards[index]) return;

    // Each card is exactly the full container width
    const containerWidth = this.cardsContainer.offsetWidth;
    const scrollLeft = containerWidth * index;

    if (typeof this.cardsContainer.scrollTo !== 'function') {
      this.cardsContainer.scrollLeft = scrollLeft;
      return;
    }

    try {
      this.cardsContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    } catch (error) {
      this.cardsContainer.scrollLeft = scrollLeft;
    }
  }
}

if (!customElements.get('testimonial-section')) {
  customElements.define('testimonial-section', Testimonial);
}
